// src/utils/xpAwards.ts
import { supabase } from './supabase';
import { createLogger } from './logger';
import { User, Session } from '@supabase/supabase-js';

const logger = createLogger({ module: 'xpAwards' });

// Cache auth state to reduce API calls
interface CachedAuth {
  user: User | null;
  session: Session | null;
  timestamp: number;
}

const authCache: CachedAuth = {
  user: null,
  session: null,
  timestamp: 0
};

const CACHE_DURATION = 60000; // 1 minute cache duration

// Function to get cached session and user, refreshing only when needed
const getCachedAuth = async (): Promise<{ user: User | null; session: Session | null; accessToken: string | null }> => {
  const now = Date.now();
  if (authCache.session && authCache.user && now - authCache.timestamp < CACHE_DURATION) {
    return { 
      user: authCache.user, 
      session: authCache.session, 
      accessToken: authCache.session.access_token 
    };
  }
  
  try {
    const { data } = await supabase.auth.getSession();
    
    authCache.session = data.session;
    authCache.user = data.session?.user || null;
    authCache.timestamp = now;
    
    return { 
      user: authCache.user, 
      session: authCache.session, 
      accessToken: authCache.session?.access_token || null 
    };
  } catch (error) {
    logger.error('Error getting cached auth:', error);
    return { user: null, session: null, accessToken: null };
  }
};

const XP_AMOUNTS = {
  review: {
    create: 50,
    update: 20,
    delete: 0
  },
  note: {
    create: 30,
    update: 15,
    delete: 0
  },
  career_card: {
    create: 40,
    update: 20,
    delete: 0
  }
} as const;

export interface XPAwardDetails {
  action: keyof typeof XP_AMOUNTS.note;
  noteId?: string;
  cardId?: string;
  reviewId?: string;
}

export const awardXP = async (type: keyof typeof XP_AMOUNTS, details: XPAwardDetails) => {
  try {
    // Use cached auth data instead of direct call
    const { user, accessToken } = await getCachedAuth();
    
    if (!user || !accessToken) {
      logger.error('User not authenticated');
      return;
    }

    const xpAmount = XP_AMOUNTS[type][details.action];
    const action_type = `${type}_${details.action}`; 
    // e.g. "note_create", "note_update", "career_card_create", etc.

    const requestBody = {
      action_type,
      metadata: {
        type,
        userId: user.id,
        xpAmount,
        noteId: details.noteId,
        cardId: details.cardId
      }
    };

    logger.debug('Request body for awarding XP:', requestBody);

    const { data, error } = await supabase.functions.invoke('award-xp', {
      body: requestBody,
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (error) {
      throw error;
    }

    logger.debug('Award XP Result:', data);
    return data;
  } catch (error) {
    logger.error('Error awarding XP:', error);
    throw error;
  }
};
